/**
 * Runtime config resolved at boot. Values come from:
 *   1. attributes on the mount element (legacy + v3 formats — see below)
 *   2. `window.SHOWPILOT_CONFIG` if set before the script loads
 *   3. URL-token (`?ptkn=<publicId>`) — legacy entry point
 *   4. hardcoded defaults
 *
 * Prioritizing the mount element lets multiple agents coexist on one page
 * (e.g. admin preview iframe) without a single global collision.
 *
 * Legacy embed compatibility: v3 widget must recognize the existing
 * legacy `<script>` tags already deployed on customer sites. The legacy
 * formats accepted (in priority order):
 *   - `data-showpilot-agent="UUID"`     (v3 canonical)
 *   - `showpilot-agent-chat="UUID"`     (legacy primary)
 *   - `storyscale-agent-chat="UUID"`    (legacy alias)
 *   - `showpilot-agent="UUID"` / `storyscale-agent="UUID"` (vue-custom-element variants)
 * For URL-token: any `?ptkn=<publicId>` query param sets a global publicId.
 */

import { DEFAULT_LAYOUT, isLayout, type Layout } from './layouts';

export interface WidgetConfig {
  publicId: string;
  apiBaseUrl: string;
  assetBaseUrl: string;
  mock: boolean;
  layout: Layout;
  /**
   * True when the mount element carried an explicit `data-showpilot-layout`
   * (or `window.SHOWPILOT_CONFIG.layout`) — i.e. the trigger/share-link
   * authored the view. When true, the widget MUST NOT override with
   * `agent.view` from /config — the trigger wins.
   */
  layoutFromAttr: boolean;
  /**
   * True on the agent-led tour DOOR (presenter shell sets
   * `data-showpilot-present`). The door is a page about the agent, not a
   * site deployment: the live face keeps its own backdrop there (no Beam
   * keying), whatever the agent's site presentation is.
   */
  presentDoor: boolean;
  /**
   * Deployment VIEW carried on the mount (data-showpilot-view) — set by
   * discover-driven autoMount so views without a 1:1 legacy layout
   * (unbound) survive to setView. Null for bare customer embeds.
   */
  viewFromAttr: string | null;
  /**
   * Presence anchor for the launcher + start bar (mv2): bottom-right
   * (default) or bottom-center. `data-showpilot-position="center"`.
   */
  position: 'right' | 'center';
  /**
   * The widget SCRIPT's own directory (never the data-showpilot-asset
   * override) — lazy code chunks (daily.js) ship next to app.js.
   */
  scriptBaseUrl: string;
  /**
   * Embed-time launch context. Sent to chat-api on session start so the
   * agent prompt + future dynamic-playlist resolver can read deployment
   * variables (e.g. `{ vin: "5TFAX..." }` on a per-vehicle inventory
   * page). Two ways to author it on the mount element:
   *   - `data-showpilot-context='{"vin":"...","make":"Toyota"}'` (JSON)
   *   - `data-context-vin="..." data-context-make="Toyota"`        (flat)
   * Flat attrs are merged ON TOP of the JSON blob — fine-grained
   * per-button overrides win over a page-level default.
   */
  launchContext: Record<string, string | number | boolean | null>;
  /**
   * Draft preview (publish model). A signed `?sp_agent_preview=` token
   * minted by the agent editor; persisted in sessionStorage so it
   * survives same-tab navigation, and forwarded on /config, /chat and
   * /decide so the server serves the operator's DRAFT instead of the
   * published revision. Null for real visitors.
   */
  agentPreviewToken: string | null;
  /**
   * Experience mode (Guided Experiences P2). Set by the /e/:slug viewer
   * shell via `window.SHOWPILOT_CONFIG.experience`. When present, the
   * widget boots the full-page viewer and auto-starts the seeded guided
   * journey (agent → assembleJourney → openContent). Null for a normal
   * embed.
   */
  experience: ExperienceConfig | null;
  /**
   * Presenter mode (docs/PRESENTER_AGENT.md). Set by the /p/?present=1
   * shell: the hub whose demo stage this page is. Forwarded as
   * `context.projectPublicId` on every chat turn and in the /voice/token
   * body — the server flips the agent into its PRESENTING stance and
   * registers the stage tools. Null for every other mount.
   */
  projectPublicId: string | null;
  /**
   * Entry model for presenter mounts: 'chat' = the text presenter seeds
   * right away · 'live' = never seed, voice auto-starts and the BOT
   * opens the demo. Null on legacy shells → the cautious 6s held seed.
   */
  presenterDelivery: 'chat' | 'live' | null;
  /**
   * Single-tour door: the /t/ link the viewer arrived through. Rides
   * chat + voice context so the presenter opens on that story.
   */
  presenterTourPublicId: string | null;
  /**
   * Start voice on boot (live delivery). Safe only because the shell's
   * Join click was the user gesture — never set on ambient mounts.
   */
  voiceAutoStart: boolean;
}

export interface ExperiencePlaylistItem {
  content_id: string;
  content_type: string;
  content_extension: string;
  name: string;
  image?: string | null;
  url?: string | null;
  /** Iframe-safe remote-video URL — without it external players refuse to load. */
  embed_url?: string | null;
}

export interface ExperienceConfig {
  publicId: string;
  name: string;
  seed: { goal?: string; entryAssetId?: string };
  /** Which mode to auto-launch in. `chat` today; voice/avatar in P2.3. */
  launchMode: 'chat' | 'voice' | 'avatar';
  /**
   * The operator's DURABLE curated playlist. Rendered as the "My
   * Playlist" listing on mount and the first item opens instantly — no
   * assembleJourney round-trip. Empty for a fully-dynamic experience.
   * For gated/reverse this holds only the RELEASED items (nothing /
   * preview); the rest arrive from /unlock after capture.
   */
  playlist: ExperiencePlaylistItem[];
  /**
   * Access gate this render (P2). `none` = show everything; `capture` =
   * lead form before ANY content; `reverse` = preview shown, lock after.
   */
  gate: 'none' | 'capture' | 'reverse';
  /** Total curated items (so the gate can say "N more after signup"). */
  totalCount: number;
  /** reverse only — free items before the lock. */
  previewCount: number;
  /** Contact fields the capture form requests. */
  fields: string[];
  /** G-1 — the content_gate form's full field list (server JSON; the
   *  gate card parses it defensively). */
  formFields?: unknown;
  /** G-1 — capture submits echo this so the after-ladder runs. */
  formId?: string | null;
}

const DEFAULT_API = 'https://api.showpilot.com';
const DEFAULT_ASSET_PROD = 'https://cdn.showpilot.com/widget/v1';

// --- Internal dev/prod backend override (ShowPilot QA only) ----------------
// Lets ShowPilot staff point a LIVE page's widget at the dev backend via a
// URL param, persisted in localStorage so it sticks as you browse the real
// site. Customers never set the param, so they never see the toggle and
// always hit prod. Guarded to ShowPilot-owned hosts so it can't be aimed at
// an arbitrary domain. No server/auth — gate is "knowing the param".
//   ?sp_env=dev   → dev backend + reveal the toggle pill
//   ?sp_env=prod  → back to prod (stay in internal mode / keep the pill)
//   ?sp_env=off   → clear everything (exit internal mode, hide the pill)
//   ?sp_api=<url> → explicit guarded host
const DEV_API_BASE = 'https://showpilot-chat-api-development.up.railway.app';
const OVERRIDE_LS_KEY = 'sp_api_override';
const INTERNAL_LS_KEY = 'sp_internal';
const ALLOWED_OVERRIDE_HOST =
  /(?:^|\.)(?:up\.railway\.app|showpilot\.(?:com|ai))$|^localhost$/i;

// localStorage access is fully guarded — some embedding contexts (sandboxed
// iframes, opaque origins) throw SecurityError on access.
function lsGet(key: string): string | null {
  try {
    return typeof window !== 'undefined' ? window.localStorage.getItem(key) : null;
  } catch {
    return null;
  }
}
function lsSet(key: string, val: string): void {
  try {
    if (typeof window !== 'undefined') window.localStorage.setItem(key, val);
  } catch {
    /* storage unavailable — ignore */
  }
}
function lsDel(key: string): void {
  try {
    if (typeof window !== 'undefined') window.localStorage.removeItem(key);
  } catch {
    /* storage unavailable — ignore */
  }
}

function guardedOrigin(url: string): string | null {
  try {
    const u = new URL(url);
    return ALLOWED_OVERRIDE_HOST.test(u.hostname) ? u.origin : null;
  } catch {
    return null;
  }
}

/** Process `?sp_env` / `?sp_api` once at boot and persist the result. */
export function applyEnvOverrideFromUrl(): void {
  if (typeof window === 'undefined') return;
  let env: string | null = null;
  let api: string | null = null;
  try {
    const p = new URLSearchParams(window.location.search);
    env = p.get('sp_env');
    api = p.get('sp_api');
  } catch {
    /* malformed search — ignore */
  }
  // The param is a one-shot activator — strip it from the address bar so
  // reloads (pill flip / exit) and navigation run off localStorage instead
  // of re-applying the param, and the override URL isn't shared/bookmarked.
  if (env !== null || api !== null) {
    try {
      const u = new URL(window.location.href);
      u.searchParams.delete('sp_env');
      u.searchParams.delete('sp_api');
      window.history.replaceState(null, '', u.toString());
    } catch {
      /* history unavailable — ignore */
    }
  }
  if (env === 'off' || api === 'off' || api === 'clear') {
    lsDel(OVERRIDE_LS_KEY);
    lsDel(INTERNAL_LS_KEY);
    return;
  }
  if (env === 'prod') {
    lsDel(OVERRIDE_LS_KEY); // back to prod, keep the pill
    return;
  }
  if (env === 'dev') {
    lsSet(OVERRIDE_LS_KEY, DEV_API_BASE);
    lsSet(INTERNAL_LS_KEY, '1');
    return;
  }
  if (api) {
    const origin = guardedOrigin(api);
    if (origin) {
      lsSet(OVERRIDE_LS_KEY, origin);
      lsSet(INTERNAL_LS_KEY, '1');
    }
  }
}

/** The persisted backend override, if any (host-guarded). */
export function getApiBaseOverride(): string | null {
  const v = lsGet(OVERRIDE_LS_KEY);
  return v ? guardedOrigin(v) : null;
}

/** True once a staffer has activated the override in this browser. */
export function isInternalMode(): boolean {
  return lsGet(INTERNAL_LS_KEY) === '1';
}

/** Imperatively set/clear the backend override (used by the dev pill). */
export function setBackendOverride(url: string | null): void {
  if (url) lsSet(OVERRIDE_LS_KEY, url);
  else lsDel(OVERRIDE_LS_KEY);
}

/** Fully exit internal mode (hide the pill). */
export function exitInternalMode(): void {
  lsDel(OVERRIDE_LS_KEY);
  lsDel(INTERNAL_LS_KEY);
}

export const SP_DEV_API_BASE = DEV_API_BASE;
export const SP_PROD_API_BASE = DEFAULT_API;

/**
 * Best-effort asset base URL detection. The widget bundle's own URL is
 * the most reliable source — assets like `videos/agent-alex.webm` and
 * `placeholders/images/agent-avatar.png` live next to `app.js` at the
 * same CDN prefix.
 *
 * Falls back to the production CDN URL when the script src can't be
 * resolved (rare, but happens with inline-bundled scripts).
 */
export function detectAssetBaseUrl(): string {
  if (typeof document === 'undefined') return DEFAULT_ASSET_PROD;
  // currentScript is the <script> tag executing right now; window.scripts
  // is searched as a fallback for IIFE bundles loaded via document.write.
  const scriptEl =
    (document.currentScript as HTMLScriptElement | null) ??
    Array.from(document.querySelectorAll<HTMLScriptElement>('script[src]')).find((s) =>
      /(\/widget\/.+|app\.js)(\?|$)/i.test(s.src),
    );
  if (scriptEl?.src) {
    // Strip filename — keep everything up to the last `/`
    const match = scriptEl.src.match(/^(.+)\/[^/]+$/);
    if (match?.[1]) return match[1];
  }
  return DEFAULT_ASSET_PROD;
}

/**
 * Attribute names checked on candidate mount elements, in priority order.
 * The first non-empty value wins.
 */
export const PUBLIC_ID_ATTRS = [
  'data-showpilot-agent',
  'showpilot-agent-chat',
  'storyscale-agent-chat',
  'showpilot-agent',
  'storyscale-agent',
] as const;

import type { PilotPromptPayload } from './components/pilot-prompt';
import type { OfferSurfacePayload } from './components/offer-surfaces';

/**
 * Shape of `window.SHOWPILOT_CONFIG` — a partial WidgetConfig, but with
 * `experience` typed loose (untrusted runtime JSON from the /e/ shell,
 * parsed defensively by readExperience) rather than strict ExperienceConfig.
 */
type GlobalConfig = Omit<Partial<WidgetConfig>, 'experience'> & {
  experience?: unknown;
  /** Set by the public tour page (/t/:publicId): this page IS the tour —
      never run host discovery to mount a chat agent alongside it. */
  tourPage?: boolean;
  /** Public tour page in its ?embed=1 (iframe) variant — telemetry says
      'embed' rather than 'share'. */
  embed?: boolean;
  /** Public tour page — the tour's public id (informational). */
  tourPublicId?: string;
};

declare global {
  interface Window {
    SHOWPILOT_CONFIG?: GlobalConfig;
    __showpilotTour?: {
      open(tourPublicId: string): void;
      close(): void;
    };
    /**
     * Personalize v1 — Pilot Prompt manual trigger for QA + the
     * future /decide wiring. Drops the proactive card. Stays
     * unwired in prod until policy decisions call it.
     *
     * Usage from console:
     *   window.__showpilotPilotPrompt({
     *     key: 'demo',
     *     agentName: 'Alex',
     *     message: 'Hey — saw you came from our email. Want a 5-min walkthrough?',
     *     chips: ['Yes, please', 'Maybe later'],
     *   });
     */
    __showpilotPilotPrompt?: (payload: PilotPromptPayload) => void;
    /** Phase 5 QA hooks — offer toast + campaign interstitial. */
    __showpilotOfferToast?: (payload: OfferSurfacePayload) => void;
    __showpilotInterstitial?: (payload: OfferSurfacePayload) => void;
  }
}

function readPublicIdFromMount(mount: HTMLElement): string | null {
  for (const attr of PUBLIC_ID_ATTRS) {
    const v = mount.getAttribute(attr);
    if (v && v.trim()) return v.trim();
  }
  return null;
}

/**
 * Parse `data-showpilot-context` (JSON) + per-key `data-context-<key>`
 * flat attrs. Flat attrs win over the JSON blob (per-button override
 * beats page default). Server-side schema in `runtime-contract.ts`
 * enforces 32 keys / 64-char keys / 1000-char values; we mirror the
 * caps here so over-budget embeds drop silently rather than fail at
 * the server. Malformed JSON is silently ignored — customer-authored
 * embed, one typo shouldn't break the widget.
 */
function readLaunchContext(
  mount: HTMLElement,
  global: GlobalConfig,
): Record<string, string | number | boolean | null> {
  const out: Record<string, string | number | boolean | null> = {};
  const ingest = (k: string, v: unknown): void => {
    if (!k || k.length > 64 || Object.keys(out).length >= 32) return;
    if (v === null || typeof v === 'boolean' || typeof v === 'number') {
      out[k] = v;
    } else if (typeof v === 'string') {
      out[k] = v.length > 1_000 ? v.slice(0, 1_000) : v;
    }
  };
  if (global.launchContext && typeof global.launchContext === 'object') {
    for (const [k, v] of Object.entries(global.launchContext)) ingest(k, v);
  }
  const raw = mount.getAttribute('data-showpilot-context');
  if (raw) {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
        for (const [k, v] of Object.entries(parsed)) ingest(k, v);
      }
    } catch {
      /* malformed JSON — skip */
    }
  }
  for (const attr of Array.from(mount.attributes)) {
    if (attr.name.startsWith('data-context-')) {
      ingest(attr.name.slice(13), attr.value);
    }
  }
  return out;
}

const AGENT_PREVIEW_SS_KEY = 'sp_agent_preview';

/**
 * Draft-preview token: URL param wins (and is persisted for the tab so
 * SPA/link navigation keeps previewing); `?sp_agent_preview=off` exits.
 * Exported for the universal-embed boot path, which runs BEFORE any
 * mount config exists (discover force-mounts the previewed agent).
 */
export function readAgentPreviewToken(): string | null {
  if (typeof window === 'undefined') return null;
  let fromUrl: string | null = null;
  try {
    fromUrl = new URLSearchParams(window.location.search).get('sp_agent_preview');
  } catch {
    /* malformed search — ignore */
  }
  try {
    if (fromUrl === 'off') {
      sessionStorage.removeItem(AGENT_PREVIEW_SS_KEY);
      return null;
    }
    if (fromUrl && fromUrl.trim()) {
      sessionStorage.setItem(AGENT_PREVIEW_SS_KEY, fromUrl.trim());
      return fromUrl.trim();
    }
    return sessionStorage.getItem(AGENT_PREVIEW_SS_KEY);
  } catch {
    return fromUrl && fromUrl !== 'off' ? fromUrl : null;
  }
}

function readPublicIdFromUrlToken(): string | null {
  if (typeof window === 'undefined' || !window.location) return null;
  try {
    const params = new URLSearchParams(window.location.search);
    const ptkn = params.get('ptkn');
    return ptkn && ptkn.trim() ? ptkn.trim() : null;
  } catch {
    return null;
  }
}

export function resolveConfig(mount: HTMLElement): WidgetConfig {
  // Mount-element attrs accept either v3 `data-showpilot-*` or legacy
  // `showpilot-agent-chat="..."` style. We honor either.
  const attrPublicId = readPublicIdFromMount(mount);
  const attrApi = mount.getAttribute('data-showpilot-api');
  const attrAsset = mount.getAttribute('data-showpilot-asset');
  const attrMock = mount.getAttribute('data-showpilot-mock');
  const attrLayout = mount.getAttribute('data-showpilot-layout');
  const attrPosition = mount.getAttribute('data-showpilot-position');

  const global = typeof window !== 'undefined' ? window.SHOWPILOT_CONFIG ?? {} : {};

  const publicId = attrPublicId ?? global.publicId ?? readPublicIdFromUrlToken();
  if (!publicId) {
    throw new Error(
      '[showpilot] agent publicId missing — set data-showpilot-agent (or legacy showpilot-agent-chat) on the mount element, or pass ?ptkn=<id> in the URL',
    );
  }

  const resolvedLayout: Layout = isLayout(attrLayout)
    ? attrLayout
    : isLayout(global.layout)
      ? global.layout
      : DEFAULT_LAYOUT;
  // Did the mount author specify a layout? Distinguishes trigger-driven
  // mounts (autoMount sets data-showpilot-layout from trigger.view) from
  // a bare customer embed where /config's agent.view should win.
  const layoutFromAttr = isLayout(attrLayout) || isLayout(global.layout);
  const attrView = mount.getAttribute('data-showpilot-view');

  return {
    publicId,
    // Internal QA override wins over everything when active.
    apiBaseUrl: getApiBaseOverride() ?? attrApi ?? global.apiBaseUrl ?? DEFAULT_API,
    assetBaseUrl: attrAsset ?? global.assetBaseUrl ?? detectAssetBaseUrl(),
    mock: attrMock === 'true' || global.mock === true,
    layout: resolvedLayout,
    layoutFromAttr,
    presentDoor: mount.getAttribute('data-showpilot-present') === '1',
    viewFromAttr: attrView && attrView.trim() ? attrView.trim() : null,
    // Presence anchor for the launcher + bar: bottom-right (default) or
    // bottom-center. Embed attr for now; the admin presence model
    // (New Trigger modal work) will surface it as an agent setting.
    position: attrPosition === 'center' ? 'center' : 'right',
    scriptBaseUrl: detectAssetBaseUrl(),
    launchContext: readLaunchContext(mount, global),
    agentPreviewToken: readAgentPreviewToken(),
    experience: readExperience(global),
    projectPublicId:
      typeof global.projectPublicId === 'string' && global.projectPublicId
        ? global.projectPublicId
        : null,
    presenterDelivery:
      global.presenterDelivery === 'chat' || global.presenterDelivery === 'live'
        ? global.presenterDelivery
        : null,
    presenterTourPublicId:
      typeof global.presenterTourPublicId === 'string' && global.presenterTourPublicId
        ? global.presenterTourPublicId
        : null,
    voiceAutoStart: global.voiceAutoStart === true,
  };
}

/**
 * Parse `window.SHOWPILOT_CONFIG.experience` (set by the /e/:slug viewer
 * shell). Defensive — a malformed blob degrades to null (normal embed),
 * never throws. Only trusts a present goal/publicId.
 */
function readExperience(global: GlobalConfig): ExperienceConfig | null {
  const raw = global.experience;
  if (!raw || typeof raw !== 'object') return null;
  const e = raw as unknown as Record<string, unknown>;
  const publicId = typeof e['publicId'] === 'string' ? e['publicId'] : null;
  if (!publicId) return null;
  const seedRaw = (e['seed'] as Record<string, unknown> | undefined) ?? {};
  const mode = e['launchMode'];
  return {
    publicId,
    name: typeof e['name'] === 'string' ? e['name'] : '',
    seed: {
      ...(typeof seedRaw['goal'] === 'string' ? { goal: seedRaw['goal'] } : {}),
      ...(typeof seedRaw['entryAssetId'] === 'string'
        ? { entryAssetId: seedRaw['entryAssetId'] }
        : {}),
    },
    launchMode:
      mode === 'voice' || mode === 'avatar' ? mode : 'chat',
    playlist: Array.isArray(e['playlist'])
      ? (e['playlist'] as unknown[])
          .filter(
            (i): i is Record<string, unknown> =>
              !!i && typeof (i as Record<string, unknown>)['content_id'] === 'string',
          )
          .map((i) => ({
            content_id: String(i['content_id']),
            content_type: typeof i['content_type'] === 'string' ? i['content_type'] : 'library',
            content_extension:
              typeof i['content_extension'] === 'string' ? i['content_extension'] : '',
            name: typeof i['name'] === 'string' ? i['name'] : '',
            image: typeof i['image'] === 'string' ? i['image'] : null,
            url: typeof i['url'] === 'string' ? i['url'] : null,
            embed_url: typeof i['embed_url'] === 'string' ? i['embed_url'] : null,
          }))
      : [],
    gate:
      e['gate'] === 'capture' || e['gate'] === 'reverse' ? e['gate'] : 'none',
    totalCount: typeof e['totalCount'] === 'number' ? e['totalCount'] : 0,
    previewCount: typeof e['previewCount'] === 'number' ? e['previewCount'] : 1,
    fields: Array.isArray(e['fields'])
      ? (e['fields'] as unknown[]).filter((f): f is string => typeof f === 'string')
      : ['name', 'email'],
    ...(e['formFields'] !== undefined ? { formFields: e['formFields'] } : {}),
    formId: typeof e['formId'] === 'string' ? e['formId'] : null,
  };
}
